import React, {ReactNode} from 'react'
import { Box, Table, TableBody, TableRow, TableCell } from '@mui/material';
import {StyledTableCaption, TableRowName} from '../../../../styledComponents/adminPage/Table'
import {
    CLEANING_TIME,
    HOURS,
    HOURS_24, RENTAL_TYPE,
    RESERVATION_TIME_UNIT_MINUTES,
    UNIT_OF_BUSINESS_SCHEDULE
} from '../../../../CodeMaster'
import InputForm from './InputForm'
import SchedulesView from './SchedulesView'
import TimeTableView from './TimeTableView'
import {
    FlexGrowContent,
    LeftRowFlexContainer,
    StretchRowFlexContainer
} from '../../../../styledComponents/FlexComponents'
import ReservationTimeSetting from './ReservationTimeSetting'
import {ReservationTimeSettingCleaningProps} from './ReservationTimeSettingCleaning'
import {
    ReservationTimeSettingMaxReservationTimeProps,
    TimeZoneSetting,
} from './ReservationTimeSettingMaxReservationTime'
import {TimeTableGroup, TimeTableItem} from './BusinessScheduleItemInterfaces'
import {
    MaxReservationTimeByTimeZone,
    MaxReservationTimeInputFormPropsWithCallback,
} from './MaxReservationTimeInputForm/MaxReservationTimeInputForm'
import equal from 'fast-deep-equal'
import FacilityOptionSetting from '../FacilityOptionSetting/FacilityOptionSetting'
import {OptionSetting} from '../FacilityOptionSetting/FacilityOptionInputForm'
import {Nullable} from '../../../../app/TypeHelpers'
import EventEmitterExAdmin from '../../../shared/pageBaseComponents/EventEmitterExAdmin'
import PPSelect from '../../../shared/forms/PPSelect'
import {addEmptyData, isNoSelect, valueOrNoSelected} from '../../../../componentHelper/selectHelper'
import HintButton from '../../../shared/pageBaseComponents/HintButton'
import {Messages} from '../../../../Messages'
import {SelectChangeEvent} from '@mui/material/Select'

interface Props {
    items: TimeTableItem[]
    cleaningSetting: ReservationTimeSettingCleaningProps
    maxReservationTimeSetting: MaxReservationTimeInputFormPropsWithCallback
    onChangeBusinessSchedule: (timeTableItems: TimeTableItem[]) => void
    stayAvailability: RENTAL_TYPE
    optionSettings: OptionSetting[]
    reserveBeforeCleaningTime: Nullable<CLEANING_TIME>
    reserveAfterCleaningTime: Nullable<CLEANING_TIME>
    onUpdateOptions: (optionSettings: OptionSetting[]) => void
    onBeforeCleaningTimeChange: (cleaningTime: Nullable<CLEANING_TIME>) => void
    onAfterCleaningTimeChange: (cleaningTime: Nullable<CLEANING_TIME>) => void
    eventEmitter: EventEmitterExAdmin
}

class BusinessScheduleSetting extends React.Component<Props> {

    private groupItems: TimeTableGroup[] = []
    private groupItemsForStraddleSchedule: TimeTableGroup[] = []

    constructor(props: Readonly<Props>) {
        super(props)

        this.handleDeleteClick = this.handleDeleteClick.bind(this)
        this.groupItems = this.createGroupItems()
        this.groupItemsForStraddleSchedule = this.createGroupItemsForStraddleSchedule()
        this.handleUpdateOptionSettings = this.handleUpdateOptionSettings.bind(this)
        this.handleReserveBeforeCleaningTimeChange = this.handleReserveBeforeCleaningTimeChange.bind(this)
        this.handleReserveAfterCleaningTimeChange = this.handleReserveAfterCleaningTimeChange.bind(this)
    }

    shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
        return !(this.props === nextProps || equal(this.props, nextProps))
    }

    handleDeleteClick(id: string): void {
        this.props.onChangeBusinessSchedule(this.props.items.filter(item => item.id !== id))
    }

    handleUpdateOptionSettings(optionSettings: OptionSetting[]): void {
        this.props.onUpdateOptions(optionSettings)
    }

    createGroupItems(): TimeTableGroup[] {
        return UNIT_OF_BUSINESS_SCHEDULE.values()
            .filter(groupItem => {
                return (groupItem !== UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS
                    && groupItem !== UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE)
            })
            .map(groupItem => {
                return {businessScheduleUnit: groupItem, title: groupItem.label}
            })
    }

    handleReserveBeforeCleaningTimeChange(event: SelectChangeEvent<unknown>): void {
        const cleaningTime: Nullable<CLEANING_TIME> = this.toCleaningTime(event.target.value)
        this.props.onBeforeCleaningTimeChange(cleaningTime)
    }

    handleReserveAfterCleaningTimeChange(event: SelectChangeEvent<unknown>): void {
        const cleaningTime: Nullable<CLEANING_TIME> = this.toCleaningTime(event.target.value)
        this.props.onAfterCleaningTimeChange(cleaningTime)
    }

    private toCleaningTime(cleaningTime: unknown): Nullable<CLEANING_TIME> {
        return isNoSelect(cleaningTime) ? null : CLEANING_TIME.codeToEnumConstants(`${cleaningTime}`)
    }

    createGroupItemsForStraddleSchedule(): TimeTableGroup[] {
        return [{
            businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE,
            title: UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE.label,
        }]
    }

    getMaxReservationTimeSettingForView(): ReservationTimeSettingMaxReservationTimeProps {
        const {
            isMultipleMaxReservationTime,
            singleMaxReservationTime,
            multipleMaxReservationUnitMinute,
            multipleMaxReservationTimes,
        } = this.props.maxReservationTimeSetting

        if (!isMultipleMaxReservationTime) {
            return {
                allDayReservationTimeMax: singleMaxReservationTime,
                timeZoneSettings: null,
            }
        }

        if (multipleMaxReservationUnitMinute === null ||
            multipleMaxReservationTimes === null ||
            multipleMaxReservationTimes.length < 1 ||
            multipleMaxReservationTimes[0] === null ||
            multipleMaxReservationTimes[0].startHour === null
        ) {
            return {
                allDayReservationTimeMax: null,
                timeZoneSettings: null,
            }
        }

        const notSettingMaxTime = multipleMaxReservationTimes.find(item => {
            const {startHour, endHour, maxTime} = item
            return (startHour !== null && endHour !== null && maxTime === null)
        })

        if (notSettingMaxTime !== undefined) {
            return {
                allDayReservationTimeMax: null,
                timeZoneSettings: null,
            }
        }

        const timeZoneSettings: TimeZoneSetting[] =
            multipleMaxReservationTimes
                .filter((item: MaxReservationTimeByTimeZone) => {
                    const {startHour, endHour, maxTime} = item
                    return (startHour !== null && endHour !== null && maxTime !== null)
                })
                .map((item) => {
                    const {startHour, endHour, maxTime} = item
                    return {
                        reservationTimeMax: maxTime || 0,
                        startTime: {
                            hour: startHour || HOURS.VOL_0,
                            minute: multipleMaxReservationUnitMinute || RESERVATION_TIME_UNIT_MINUTES.VOL_0,
                        },
                        endTime: {
                            hour: endHour || HOURS.VOL_0,
                            minute: multipleMaxReservationUnitMinute || RESERVATION_TIME_UNIT_MINUTES.VOL_0,
                        },
                    }
                })
        return {
            allDayReservationTimeMax: null,
            timeZoneSettings: timeZoneSettings,
        }
    }

    render(): ReactNode {
        const isStraddleSchedule = (
            this.props.items.length === 1
            && this.props.items[0].businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE
        )

        const groupItems = (isStraddleSchedule) ? this.groupItemsForStraddleSchedule : this.groupItems

        return (
            <>
                <Table>
                    <StyledTableCaption>時間貸し施設の設定</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName>オプション設定</TableRowName>
                            <TableCell>
                                <FacilityOptionSetting
                                    options={this.props.optionSettings}
                                    stayAvailability={this.props.stayAvailability}
                                    eventEmitter={this.props.eventEmitter}
                                    onUpdateOptions={this.handleUpdateOptionSettings}
                                />
                            </TableCell>
                        </TableRow>
                        <InputForm
                            timeTableItems={this.props.items}
                            maxReservationTimeInputFormProps={this.props.maxReservationTimeSetting}
                            onChangeBusinessSchedule={this.props.onChangeBusinessSchedule}
                        />
                        <TableRow>
                            <TableRowName>掃除の時間</TableRowName>
                            <TableCell>
                                <LeftRowFlexContainer>
                                    <label>
                                        <LeftRowFlexContainer>
                                            予約前
                                            <PPSelect
                                                data={addEmptyData(CLEANING_TIME.values())}
                                                value={valueOrNoSelected(this.props.reserveBeforeCleaningTime)}
                                                onChange={this.handleReserveBeforeCleaningTimeChange}
                                            />
                                        </LeftRowFlexContainer>
                                    </label>
                                    <Box marginLeft={'30px'}/>
                                    <label>
                                        <LeftRowFlexContainer>
                                            予約後
                                            <PPSelect
                                                data={addEmptyData(CLEANING_TIME.values())}
                                                value={valueOrNoSelected(this.props.reserveAfterCleaningTime)}
                                                onChange={this.handleReserveAfterCleaningTimeChange}
                                            />
                                            分
                                        </LeftRowFlexContainer>
                                    </label>
                                    <HintButton message={Messages.HFACA0004.message}/>
                                </LeftRowFlexContainer>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <StretchRowFlexContainer>
                    <ReservationTimeSetting
                        cleaningSetting={this.props.cleaningSetting}
                        maxReservationTimeSetting={this.getMaxReservationTimeSettingForView()}
                    />
                    <FlexGrowContent>
                        <SchedulesView
                            groupItems={groupItems}
                            items={this.props.items}
                            onDeleteClick={this.handleDeleteClick}
                        />
                    </FlexGrowContent>
                </StretchRowFlexContainer>
                {
                    (!isStraddleSchedule) &&
                    <TimeTableView
                        groupItems={groupItems}
                        defaultTimeStart={{hour: HOURS_24.VOL_0, minute: RESERVATION_TIME_UNIT_MINUTES.VOL_0}}
                        defaultTimeEnd={{hour: HOURS_24.VOL_24, minute: RESERVATION_TIME_UNIT_MINUTES.VOL_0}}
                        pixelPer5Minutes={2}
                        items={this.props.items}
                    />
                }
            </>
        )
    }
}

export default BusinessScheduleSetting
